import React from 'react';
import styled from 'styled-components';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'success' | 'premium';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

const StyledButton = styled.button<{
  $variant: string;
  $size: string;
  $disabled: boolean;
  $loading: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.$size === 'sm' ? '0.25rem' : '0.5rem'};
  padding: ${props => {
    switch (props.$size) {
      case 'sm': return '0.5rem 1rem';
      case 'lg': return '0.875rem 2rem';
      default: return '0.625rem 1.25rem';
    }
  }};
  border-radius: ${props => props.theme.radius};
  font-weight: ${props => props.theme.fontMedium || '500'};
  font-size: ${props => {
    switch (props.$size) {
      case 'sm': return '0.875rem';
      case 'lg': return '1.125rem';
      default: return '1rem';
    }
  }};
  line-height: 1;
  transition: ${props => props.theme.transitionSmooth};
  cursor: ${props => props.$disabled || props.$loading ? 'not-allowed' : 'pointer'};
  border: 1px solid transparent;
  text-decoration: none;
  white-space: nowrap;
  position: relative;
  opacity: ${props => props.$disabled ? 0.5 : 1};

  /* Remove all focus borders */
  &:focus-visible {
    outline: none !important;
  }
  
  &:focus {
    outline: none !important;
    border: 1px solid transparent !important;
  }
  
  &:active {
    outline: none !important;
    border: 1px solid transparent !important;
  }

  /* Variant styles */
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, ${props.theme.primary}, ${props.theme.primaryGlow || props.theme.primary});
          color: ${props.theme.primaryForeground};
          border-color: ${props.theme.primary};
          box-shadow: ${props.theme.shadowElegant};
          
          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadowLg || props.theme.shadowElegant};
          }
          
          &:active:not(:disabled) {
            transform: translateY(0);
          }
        `;
      
      case 'secondary':
        return `
          background-color: ${props.theme.secondary};
          color: ${props.theme.secondaryForeground};
          border-color: ${props.theme.secondary};
          
          &:hover:not(:disabled) {
            background-color: ${props.theme.secondary}E6;
            transform: translateY(-1px);
            box-shadow: ${props.theme.shadowSoft};
          }
          
          &:active:not(:disabled) {
            transform: translateY(0);
          }
        `;
      
      case 'outline':
        return `
          background-color: transparent;
          color: ${props.theme.foreground};
          border-color: ${props.theme.border};
          
          &:hover:not(:disabled) {
            background-color: ${props.theme.muted};
            border-color: ${props.theme.ring};
          }
        `;
      
      case 'ghost':
        return `
          background-color: transparent;
          color: ${props.theme.foreground};
          border-color: transparent;
          
          &:hover:not(:disabled) {
            background-color: ${props.theme.muted};
          }
        `;
      
      case 'destructive':
        return `
          background-color: ${props.theme.destructive};
          color: ${props.theme.destructiveForeground};
          border-color: ${props.theme.destructive};
          
          &:hover:not(:disabled) {
            background-color: ${props.theme.destructive}E6;
            transform: translateY(-1px);
            box-shadow: ${props.theme.shadowSoft};
          }
          
          &:active:not(:disabled) {
            transform: translateY(0);
          }
        `;
      
      case 'success':
        return `
          background-color: ${props.theme.success};
          color: ${props.theme.successForeground};
          border-color: ${props.theme.success};
          box-shadow: ${props.theme.shadowSoft};
          
          &:hover:not(:disabled) {
            background-color: ${props.theme.success}E6;
            transform: translateY(-1px);
            box-shadow: ${props.theme.shadowSoft};
          }
          
          &:active:not(:disabled) {
            transform: translateY(0);
          }
        `;
      
      case 'premium':
        return `
          background: linear-gradient(135deg, ${props.theme.primary}, ${props.theme.primaryGlow || props.theme.primary});
          color: ${props.theme.primaryForeground};
          border: 1px solid ${props.theme.primaryGlow || props.theme.primary}30;
          box-shadow: ${props.theme.shadowElegant};
          
          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: ${props.theme.shadowLg || props.theme.shadowElegant};
          }
          
          &:active:not(:disabled) {
            transform: translateY(0);
          }
        `;
      
      default:
        return '';
    }
  }}

  /* Loading state */
  ${props => props.$loading && `
    opacity: 0.7;
    pointer-events: none;
    
    &::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 16px;
      height: 16px;
      margin: -8px 0 0 -8px;
      border: 2px solid transparent;
      border-top-color: currentColor;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  `}


  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  children,
  onClick,
  type = 'button',
  className,
  ...props
}, ref) => {
  return (
    <StyledButton
      ref={ref}
      $variant={variant}
      $size={size}
      $disabled={disabled}
      $loading={loading}
      onClick={onClick}
      type={type}
      disabled={disabled || loading}
      className={className}
      {...props}
    >
      {loading ? null : children}
    </StyledButton>
  );
});

Button.displayName = 'Button'; 